// [HŌJIN — SÉCURITÉ P0-quater ③] Client SSE basé sur fetch, pour NE PLUS passer le JWT en query string.
// EventSource natif ne permet pas d'en-tête custom → le token circulait en clair dans l'URL du /stream
// (visible logs / historique / Referer, signalé 2× par CCC). Ici on lit le flux via fetch + ReadableStream
// avec `Authorization: Bearer` — le JWT ne quitte jamais les en-têtes. Le backend accepte déjà ce header
// (verifyTokenFromQuery, fallback Authorization).
//
// Le shim est COMPATIBLE EventSource : il expose addEventListener / onopen / onerror / close et dispatche
// de VRAIS objets `MessageEvent` (avec .data et .type) → le consommateur (use-deliberation) ne change pas
// d'un iota dans ses ~30 handlers. Il parse les trames SSE (`event:`/`data:`/`id:`), gère le multi-ligne
// `data:`, ignore les commentaires `:`, et suit le dernier `id` (Last-Event-ID pour la reprise).

type MsgHandler = (ev: MessageEvent) => void;

export class FetchEventSource {
  onopen: (() => void) | null = null;
  onerror: (() => void) | null = null;
  /** Emplacement libre utilisé par le hook pour accrocher son nettoyage (parité avec l'ancien EventSource). */
  __cleanup__?: () => void;
  private listeners = new Map<string, Set<MsgHandler>>();
  private ctl = new AbortController();
  private closed = false;
  private lastEventId: string | null = null;

  constructor(url: string, opts: { headers?: Record<string, string>; lastEventId?: string | null } = {}) {
    this.lastEventId = opts.lastEventId ?? null;
    void this.run(url, opts.headers ?? {});
  }

  addEventListener(type: string, handler: MsgHandler): void {
    let set = this.listeners.get(type);
    if (!set) { set = new Set(); this.listeners.set(type, set); }
    set.add(handler);
  }

  close(): void {
    this.closed = true;
    try { this.ctl.abort(); } catch { /* déjà abort */ }
  }

  private dispatch(type: string, data: string): void {
    const set = this.listeners.get(type);
    if (!set || set.size === 0) return;
    // VRAI MessageEvent → .data et .type comme un EventSource natif (aucun cast à changer côté hook).
    const ev = new MessageEvent(type, { data, lastEventId: this.lastEventId ?? "" });
    for (const h of set) { try { h(ev); } catch { /* un handler ne casse pas le flux */ } }
  }

  private emitError(): void {
    if (this.closed) return; // fermé volontairement → pas d'onerror parasite
    this.onerror?.();
  }

  // Parse un bloc de trame SSE (séparé par une ligne vide) et le dispatche.
  private handleBlock(block: string): void {
    if (!block) return;
    let event = "message";
    const dataLines: string[] = [];
    for (const rawLine of block.split("\n")) {
      const line = rawLine.replace(/\r$/, "");
      if (!line || line.startsWith(":")) continue; // ligne vide / commentaire keep-alive
      const idx = line.indexOf(":");
      const field = idx === -1 ? line : line.slice(0, idx);
      let value = idx === -1 ? "" : line.slice(idx + 1);
      if (value.startsWith(" ")) value = value.slice(1); // SSE : un seul espace après « : »
      if (field === "event") event = value;
      else if (field === "data") dataLines.push(value);
      else if (field === "id") this.lastEventId = value;
      // field === "retry" ignoré (la reprise est gérée côté hook via reconnectNonce)
    }
    if (dataLines.length > 0) this.dispatch(event, dataLines.join("\n"));
  }

  private async run(url: string, headers: Record<string, string>): Promise<void> {
    try {
      const h: Record<string, string> = { Accept: "text/event-stream", ...headers };
      if (this.lastEventId) h["Last-Event-ID"] = this.lastEventId;
      const res = await fetch(url, { headers: h, signal: this.ctl.signal, cache: "no-store" });
      if (!res.ok || !res.body) { this.emitError(); return; }
      this.onopen?.();
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let sep: number;
        while ((sep = firstBlankLine(buffer)) !== -1) {
          const block = buffer.slice(0, sep);
          buffer = buffer.slice(indexAfterBlank(buffer, sep));
          this.handleBlock(block);
        }
      }
      // Flux terminé (le backend a fait res.end()) → traité comme un décrochage : le hook lit l'état
      // terminal via les events déjà reçus (error severity:fatal / complete) ou via le polling.
      this.emitError();
    } catch {
      this.emitError(); // abort (close) OU erreur réseau → onerror ; le hook décide (relance/polling)
    }
  }
}

/** Index du début de la première ligne vide (fin d'une trame). -1 si aucune. */
function firstBlankLine(buf: string): number {
  const a = buf.indexOf("\n\n");
  const b = buf.indexOf("\r\n\r\n");
  if (a === -1) return b === -1 ? -1 : b;
  if (b === -1) return a;
  return Math.min(a, b);
}
/** Index juste après le séparateur de trame commençant à `sep`. */
function indexAfterBlank(buf: string, sep: number): number {
  return buf.startsWith("\r\n\r\n", sep) ? sep + 4 : sep + 2;
}
